import {
  ArrowUpRight,
  Bell,
  CheckCircle2,
  ClipboardCheck,
  Eye,
  MapPin,
  MessageCircle,
  MessageSquare,
  Zap,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { parseTimestamp } from '@/uitilites/utility';

const activityTime = (value) => {
  const parsed = parseTimestamp(value);
  return parsed && !Number.isNaN(parsed.getTime()) ? parsed.getTime() : 0;
};

export const sortLiveActivityItems = (items = []) => [...items].sort((left, right) => (
  activityTime(right?.created_at) - activityTime(left?.created_at)
));

export const formatLiveActivityDateTime = (value) => {
  const parsed = parseTimestamp(value);
  if (!parsed || Number.isNaN(parsed.getTime())) return 'Time unavailable';
  return parsed.toLocaleString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
};

const activityPresentation = (entry = {}) => {
  if (entry.type === 'related_incident') {
    const related = entry.incident || {};
    return {
      Icon: entry.relationship === 'same_location' ? MapPin : Zap,
      label: entry.relationship_label || (entry.relationship === 'same_location' ? 'Same location' : 'Related incident'),
      title: `INC-${String(related.id || '').slice(-8).toUpperCase() || 'UNKNOWN'} · ${related.incident_type || 'Incident'}`,
      description: entry.location || 'Open the incident report',
      iconClass: 'bg-red-600 text-white',
    };
  }
  if (entry.type === 'comment') {
    return {
      Icon: MessageCircle,
      label: 'Card discussion',
      title: entry.card_name || 'Timeline card',
      description: entry.message || 'New discussion activity',
      iconClass: 'bg-violet-600 text-white',
    };
  }
  if (entry.type === 'corrective_action') {
    return {
      Icon: ClipboardCheck,
      label: 'Corrective action',
      title: entry.author || 'App user',
      description: entry.message || 'Corrective action updated',
      iconClass: 'bg-emerald-600 text-white',
    };
  }
  if (entry.type === 'observation') {
    return {
      Icon: Eye,
      label: 'Observation',
      title: entry.author || 'App user',
      description: entry.message || 'Observation added',
      iconClass: 'bg-amber-500 text-white',
    };
  }
  if (entry.type === 'message') {
    return {
      Icon: MessageSquare,
      label: entry.channel_name ? `Message · ${entry.channel_name}` : 'Message',
      title: entry.author || 'App user',
      description: entry.message || 'Shared an attachment',
      iconClass: 'bg-sky-600 text-white',
    };
  }
  if (entry.type === 'responder_activity') {
    const completed = ['verification_submitted', 'task_completed', 'action_completed'].includes(entry.interaction_kind);
    return {
      Icon: completed ? CheckCircle2 : Bell,
      label: String(entry.interaction_kind || 'App response').replace(/_/g, ' '),
      title: entry.author || 'App user',
      description: entry.message || 'Timeline response received',
      iconClass: completed ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white',
    };
  }
  return {
    Icon: Bell,
    label: 'Live activity',
    title: entry.author || 'App user',
    description: entry.message || 'New activity received',
    iconClass: 'bg-red-600 text-white',
  };
};

const LiveActivityTimelineModal = ({ items = [], onSelect }) => {
  const [open, setOpen] = useState(false);
  const sortedItems = useMemo(() => sortLiveActivityItems(items), [items]);

  const selectItem = (entry) => {
    setOpen(false);
    onSelect?.(entry);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="bg-white text-red-600 px-3 py-1 rounded-full text-xs font-bold mr-4 flex-shrink-0 transition hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-red-600"
        title="Open the live activity timeline"
        data-testid="live-activity-chip"
      >
        {sortedItems.length} LIVE
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-h-[86vh] max-w-3xl gap-0 overflow-hidden border-0 bg-slate-50 p-0 [&>button]:text-white"
          data-testid="live-activity-dialog"
        >
          <DialogHeader className="bg-gradient-to-r from-red-600 via-red-600 to-rose-600 px-6 py-5 pr-12 text-left text-white">
            <div className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-red-100">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-70" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-white" />
              </span>
              Operational live feed
            </div>
            <DialogTitle className="flex items-center gap-3 text-2xl text-white">
              Live activity timeline
              <span className="rounded-full bg-white/15 px-2.5 py-1 text-xs font-bold ring-1 ring-white/25">
                {sortedItems.length}
              </span>
            </DialogTitle>
            <DialogDescription className="max-w-2xl text-sm text-red-100">
              App responses, messages, card activity, related reports, and incidents reported at the same location — newest first.
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[64vh] overflow-y-auto px-5 py-5 sm:px-7" data-testid="live-activity-list">
            {sortedItems.length === 0 ? (
              <div className="flex min-h-52 flex-col items-center justify-center rounded-2xl border border-dashed border-red-200 bg-white px-6 text-center">
                <Bell className="mb-3 h-9 w-9 text-red-300" />
                <p className="font-semibold text-slate-800">No live activity yet</p>
                <p className="mt-1 text-sm text-slate-500">New app-user and nearby incident activity will appear here in real time.</p>
              </div>
            ) : (
              <div className="relative space-y-3">
                <div className="absolute bottom-5 left-[19px] top-5 w-px bg-gradient-to-b from-red-500 via-red-200 to-transparent" aria-hidden="true" />
                {sortedItems.map((entry, index) => {
                  const presentation = activityPresentation(entry);
                  const Icon = presentation.Icon;
                  return (
                    <div className="relative flex gap-3" key={`${entry.type || 'activity'}-${entry.id || index}-${index}`}>
                      <div className={`relative z-10 mt-4 flex h-10 w-10 shrink-0 items-center justify-center rounded-full shadow-sm ring-4 ring-slate-50 ${presentation.iconClass}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <button
                        type="button"
                        onClick={() => selectItem(entry)}
                        className="group min-w-0 flex-1 rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-red-200 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                        data-testid={`live-activity-row-${entry.type || 'activity'}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="mb-1.5 flex flex-wrap items-center gap-2">
                              <span className="rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-red-700">
                                {presentation.label}
                              </span>
                              <time className="text-[11px] font-medium text-slate-400" dateTime={String(entry.created_at || '')}>
                                {formatLiveActivityDateTime(entry.created_at)}
                              </time>
                            </div>
                            <p className="truncate text-sm font-bold text-slate-900">{presentation.title}</p>
                            <p className="mt-1 line-clamp-2 text-sm leading-5 text-slate-600">{presentation.description}</p>
                          </div>
                          <ArrowUpRight className="mt-1 h-4 w-4 shrink-0 text-slate-300 transition group-hover:text-red-500" />
                        </div>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LiveActivityTimelineModal;
